import React, { useEffect, useState } from "react";
import api from "../../../api";
import { Loader } from "../loader";
import moment from "moment";
import { Pagination, Stack } from "@mui/material";
import "./WaqarTradeMonitor.css";

const formatDuration = (secs) => {
  if (secs == null) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const LiveSessionStats = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Separate, independent dataset covering ALL sessions (every page merged),
  // used only to compute accurate aggregate stats — the API has no aggregate
  // endpoint, so this is a frontend-only workaround. Kept apart from `data`/
  // `page` so the table's own pagination is unaffected.
  const [allSessions, setAllSessions] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    load(page);
  }, [page]);

  useEffect(() => {
    loadAllForStats();
  }, []);

  const load = async (p) => {
    setLoader(true);
    try {
      const res = await api.getLiveSessionStats(p);
      if (res?.success) {
        setData(res.data || []);
        setTotalPages(res.totalPages || 1);
        setTotal(res.total || 0);
      }
    } catch (e) {
      console.error(e);
    }
    setLoader(false);
  };

  const loadAllForStats = async () => {
    setStatsLoading(true);
    try {
      const first = await api.getLiveSessionStats(1);
      if (first?.success) {
        const pages = first.totalPages || 1;
        let merged = first.data || [];
        if (pages > 1) {
          const rest = await Promise.all(
            Array.from({ length: pages - 1 }, (_, i) =>
              api.getLiveSessionStats(i + 2),
            ),
          );
          rest.forEach((res) => {
            if (res?.success) merged = merged.concat(res.data || []);
          });
        }
        setAllSessions(merged);
      }
    } catch (e) {
      console.error(e);
    }
    setStatsLoading(false);
  };

  // Aggregate summary computed from ALL sessions (allSessions), not just the
  // currently-loaded table page.
  const withDuration = allSessions.filter((s) => s.durationSeconds != null);
  const avgDurationSecs =
    withDuration.length === 0
      ? null
      : Math.round(
          withDuration.reduce((acc, s) => acc + s.durationSeconds, 0) /
            withDuration.length,
        );
  const hostCount = new Set(
    allSessions
      .map((s) => s.conductedBy?.email || s.conductedBy?.name)
      .filter(Boolean),
  ).size;

  return (
    <div className="waqar-tm">
      {loader && <Loader />}

      {!loader && (
        <>
          <div className="waqar-tm-hero-grid">
            <div className="waqar-tm-hero-card waqar-tm-hero--teal">
              <span className="waqar-tm-hero-label">Sessions logged</span>
              <span className="waqar-tm-hero-value">{total}</span>
            </div>
            <div className="waqar-tm-hero-card waqar-tm-hero--blue">
              <span className="waqar-tm-hero-label">Avg duration</span>
              <span className="waqar-tm-hero-value">
                {statsLoading
                  ? "…"
                  : avgDurationSecs != null
                    ? formatDuration(avgDurationSecs)
                    : "—"}
              </span>
            </div>
            <div className="waqar-tm-hero-card waqar-tm-hero--red">
              <span className="waqar-tm-hero-label">Hosts</span>
              <span className="waqar-tm-hero-value">
                {statsLoading ? "…" : hostCount}
              </span>
            </div>
          </div>

          <div className="waqar-tm-section">
            <div className="waqar-tm-table-card">
              <div className="waqar-tm-section-header">
                <span className="waqar-tm-section-title">
                  Live Session Stats
                </span>
                <span className="waqar-tm-count-badge">{total}</span>
                <span className="waqar-tm-section-meta">
                  showing {data.length} of {total}
                </span>
              </div>
              <div className="waqar-tm-table-scroll">
                <table className="waqar-tm-table">
                  <thead>
                    <tr className="waqar-tm-thead-row">
                      <th className="waqar-tm-th">Title</th>
                      <th className="waqar-tm-th">Conducted By</th>
                      <th className="waqar-tm-th">Start (Event Time)</th>
                      <th className="waqar-tm-th">End Time</th>
                      <th className="waqar-tm-th">Duration</th>
                      <th className="waqar-tm-th">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="waqar-tm-empty-row">
                          No sessions found
                        </td>
                      </tr>
                    ) : (
                      data.map((s) => (
                        <tr key={s._id} className="waqar-tm-row">
                          <td className="waqar-tm-td">
                            <span className="waqar-tm-symbol">
                              {s.title || "—"}
                            </span>
                          </td>
                          <td className="waqar-tm-td">
                            {s.conductedBy ? (
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {s.conductedBy.name || "—"}
                                </div>
                                <div className="waqar-tm-mono waqar-tm-muted">
                                  {s.conductedBy.email}
                                </div>
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="waqar-tm-td">
                            {s.eventTime ? (
                              <div>
                                <div>
                                  {moment(s.eventTime).format("MMM D, YYYY")}
                                </div>
                                <div className="waqar-tm-mono waqar-tm-muted">
                                  {moment(s.eventTime).format("h:mm A")}
                                </div>
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="waqar-tm-td">
                            {s.endedAt ? (
                              <div>
                                <div>
                                  {moment(s.endedAt).format("MMM D, YYYY")}
                                </div>
                                <div className="waqar-tm-mono waqar-tm-muted">
                                  {moment(s.endedAt).format("h:mm A")}
                                </div>
                              </div>
                            ) : (
                              <span className="waqar-tm-muted">
                                Not ended yet
                              </span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-duration">
                            {s.durationSeconds != null
                              ? formatDuration(s.durationSeconds)
                              : "—"}
                          </td>
                          <td className="waqar-tm-td">
                            <span
                              className={`waqar-tm-pill ${
                                s.isEnded
                                  ? "waqar-tm-pill--tp"
                                  : "waqar-tm-pill--closed"
                              }`}
                            >
                              {s.isEnded ? "Ended" : "Active"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    size="small"
                  />
                </Stack>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveSessionStats;
